import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RefreshCw } from "lucide-react";
import { getWithTokenAPI, postWithTokenAPI } from "../Util/Api";
import "../Style/Pages/VersionControl.css";

const DEFAULT_FORM = {
  currentVersion: "",
  softUpdateVersion: "",
  forceUpdateVersion: "",
  softUpdateMessage: "",
  forceUpdateMessage: "",
  isMaintenanceMode: false,
  maintenanceMessage: "",
};

const APP_TYPES = [
  { value: "user", label: "Customer" },
  { value: "driver", label: "Driver" },
];

const VersionControl = () => {
  const auth = useSelector((state) => state.auth);
  const token = auth.token;
  const permission = auth.permission;
  const canUpdate = permission?.versioncontrol?.update;

  const [appType, setAppType] = useState("user");
  const [versionData, setVersionData] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const fetchVersionControl = async () => {
    setLoading(true);
    setFetchError("");
    try {
      const response = await getWithTokenAPI(
        token,
        `/version-control?platform=android&appVersion=1&type=${appType}`,
      );

      if (response?.statusCode === 200 && response?.data) {
        const data = response.data;
        setVersionData(data);
        setForm({
          currentVersion: data.currentVersion || "",
          softUpdateVersion: data.softUpdateVersion || "",
          forceUpdateVersion: data.forceUpdateVersion || "",
          softUpdateMessage: data.messages?.softUpdateMessage || "",
          forceUpdateMessage: data.messages?.forceUpdateMessage || "",
          isMaintenanceMode: !!data.maintenance?.isMaintenanceMode,
          maintenanceMessage: data.maintenance?.maintenanceMessage || "",
        });
      } else {
        setVersionData(null);
        setForm(DEFAULT_FORM);
      }
    } catch (error) {
      console.error("Error fetching version control settings:", error);
      setVersionData(null);
      setFetchError("Failed to load version control settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersionControl();
  }, [token, appType]);

  const handleAppTypeChange = (nextType) => {
    if (nextType === appType) return;
    setAppType(nextType);
    setErrors({});
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleMaintenanceToggle = () => {
    setForm((prev) => ({
      ...prev,
      isMaintenanceMode: !prev.isMaintenanceMode,
    }));
    setErrors((prev) => ({ ...prev, maintenanceMessage: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.currentVersion.trim())
      nextErrors.currentVersion = "Current version is required";
    if (!form.softUpdateVersion.trim())
      nextErrors.softUpdateVersion = "Soft update version is required";
    if (!form.forceUpdateVersion.trim())
      nextErrors.forceUpdateVersion = "Force update version is required";
    if (!form.softUpdateMessage.trim())
      nextErrors.softUpdateMessage = "Soft update message is required";
    if (!form.forceUpdateMessage.trim())
      nextErrors.forceUpdateMessage = "Force update message is required";
    if (form.isMaintenanceMode && !form.maintenanceMessage.trim())
      nextErrors.maintenanceMessage =
        "Maintenance message is required when maintenance mode is enabled";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canUpdate) return;
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        platforms: {
          android: {
            currentVersion: form.currentVersion.trim(),
            forceUpdateVersion: form.forceUpdateVersion.trim(),
            softUpdateVersion: form.softUpdateVersion.trim(),
            softUpdateMessage: form.softUpdateMessage.trim(),
            forceUpdateMessage: form.forceUpdateMessage.trim(),
            isMaintenanceMode: form.isMaintenanceMode,
            maintenanceMessage: form.maintenanceMessage.trim(),
          },
        },
      };

      const response = await postWithTokenAPI(
        token,
        `/version-control/create?type=${appType}`,
        payload,
      );

      if (
        response?.statusCode === 200 ||
        response?.statusCode === 201 ||
        response?.status === "success"
      ) {
        toast.success(
          versionData
            ? "Version control settings updated successfully"
            : "Version control settings created successfully",
        );
        fetchVersionControl();
      } else {
        toast.error(
          response?.message || "Failed to save version control settings",
        );
      }
    } catch (error) {
      console.error("Error saving version control settings:", error);
      toast.error("Failed to save version control settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="vc-page">
      <div className="vc-layout">
        {/* RIGHT: FORM */}
        <main className="vc-main">
          <div className="vc-type-tabs">
            {APP_TYPES.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`vc-type-tab${appType === option.value ? " active" : ""}`}
                onClick={() => handleAppTypeChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {fetchError ? (
            <div className="vc-error-state">
              <p>{fetchError}</p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={fetchVersionControl}
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          ) : (
            <>
              {!loading && !versionData && (
                <div className="vc-empty-state">
                  No version control configuration found for{" "}
                  {APP_TYPES.find((option) => option.value === appType)?.label}{" "}
                  Android yet. Fill in the details below to create one.
                </div>
              )}

              <form className="vc-form" onSubmit={handleSave}>
                <div className="vc-card">
                  <h3 className="vc-card-title">Update Versions</h3>
                  <div className="vc-card-grid">
                    <div className="form-groupp">
                      <label>Current Version *</label>
                      <input
                        type="text"
                        className="form-input-title"
                        value={form.currentVersion}
                        disabled={!canUpdate || loading}
                        onChange={(e) =>
                          handleFieldChange("currentVersion", e.target.value)
                        }
                        placeholder="e.g. 1.0"
                      />
                      {errors.currentVersion && (
                        <span className="error">{errors.currentVersion}</span>
                      )}
                    </div>

                    <div className="form-groupp">
                      <label>Force Update Version *</label>
                      <input
                        type="text"
                        className="form-input-title"
                        value={form.forceUpdateVersion}
                        disabled={!canUpdate || loading}
                        onChange={(e) =>
                          handleFieldChange(
                            "forceUpdateVersion",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. 1"
                      />
                      {errors.forceUpdateVersion && (
                        <span className="error">
                          {errors.forceUpdateVersion}
                        </span>
                      )}
                    </div>

                    <div className="form-groupp">
                      <label>Soft Update Version *</label>
                      <input
                        type="text"
                        className="form-input-title"
                        value={form.softUpdateVersion}
                        disabled={!canUpdate || loading}
                        onChange={(e) =>
                          handleFieldChange(
                            "softUpdateVersion",
                            e.target.value,
                          )
                        }
                        placeholder="e.g. 2"
                      />
                      {errors.softUpdateVersion && (
                        <span className="error">
                          {errors.softUpdateVersion}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="vc-card">
                  <h3 className="vc-card-title">Update Messages</h3>

                  <div className="form-groupp">
                    <label>Soft Update Message *</label>
                    <textarea
                      className="form-textarea"
                      value={form.softUpdateMessage}
                      disabled={!canUpdate || loading}
                      onChange={(e) =>
                        handleFieldChange("softUpdateMessage", e.target.value)
                      }
                      placeholder="Message shown to Android users when a soft update is available"
                    />
                    {errors.softUpdateMessage && (
                      <span className="error">
                        {errors.softUpdateMessage}
                      </span>
                    )}
                  </div>

                  <div className="form-groupp">
                    <label>Force Update Message *</label>
                    <textarea
                      className="form-textarea"
                      value={form.forceUpdateMessage}
                      disabled={!canUpdate || loading}
                      onChange={(e) =>
                        handleFieldChange(
                          "forceUpdateMessage",
                          e.target.value,
                        )
                      }
                      placeholder="Message shown to Android users when a force update is required"
                    />
                    {errors.forceUpdateMessage && (
                      <span className="error">
                        {errors.forceUpdateMessage}
                      </span>
                    )}
                  </div>
                </div>

                <div className="vc-card">
                  <div className="vc-card-header-row">
                    <div>
                      <h3 className="vc-card-title">Maintenance Mode</h3>
                      <p className="vc-card-subtitle">
                        Temporarily block Android app access with a custom
                        message.
                      </p>
                    </div>
                    <label className="vc-switch">
                      <input
                        type="checkbox"
                        checked={form.isMaintenanceMode}
                        disabled={!canUpdate || loading}
                        onChange={handleMaintenanceToggle}
                      />
                      <span className="vc-switch-slider"></span>
                    </label>
                  </div>

                  <div className="form-groupp">
                    <label>
                      Maintenance Message {form.isMaintenanceMode && "*"}
                    </label>
                    <textarea
                      className="form-textarea"
                      value={form.maintenanceMessage}
                      disabled={!canUpdate || loading || !form.isMaintenanceMode}
                      onChange={(e) =>
                        handleFieldChange(
                          "maintenanceMessage",
                          e.target.value,
                        )
                      }
                      placeholder="Message shown to Android users while maintenance mode is enabled"
                    />
                    {errors.maintenanceMessage && (
                      <span className="error">
                        {errors.maintenanceMessage}
                      </span>
                    )}
                  </div>
                </div>

                {canUpdate && (
                  <div className="vc-form-actions">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={saving || loading}
                    >
                      {saving ? (
                        <span className="loading-spinner-small"></span>
                      ) : versionData ? (
                        "Update Settings"
                      ) : (
                        "Save Settings"
                      )}
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default VersionControl;
